import React, { useState, useEffect } from 'react';
import Balancer from "react-wrap-balancer";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLaptopCode } from '@fortawesome/free-solid-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import work from "../../data/work.json";
import workOrder from "../../data/work-order.json";


import { Container, Row, Col, Button, Modal } from "react-bootstrap";


const BASE_MIN_VISIBLE = 9;
const TABLET_MIN_VISIBLE = 6; // ensures 2x2 grid on tablet widths
const TAG_FILTER_CATEGORIES = [
  {
    key: "web-development",
    label: "Web Development",
    fields: ["frameworks"]
  },
  {
    key: "graphic-design",
    label: "Graphic Design",
    fields: ["scope", "capabilities"]
  }
];

const getNonEmptyItems = (items = []) => items.filter((item) => item && item.trim() !== "");
const getTrimmedValue = (value = "") => value.trim();
const getProjectTagsByFields = (project = {}, fields = []) => {
  const tags = fields.flatMap((field) => getNonEmptyItems(project[field]));
  return [...new Set(tags)];
};
const getProjectTags = (project = {}) => {
  const capabilities = getNonEmptyItems(project.capabilities);
  const scope = getNonEmptyItems(project.scope);
  const frameworks = getNonEmptyItems(project.frameworks);

  return [...new Set([...capabilities, ...scope, ...frameworks])];
};
const getImageFit = (value = "") => (value === "fill" ? "fill" : "contain");
const getCategoryPillClass = (categoryKey = "") => {
  if (categoryKey === "web-development") {
    return "red-pill";
  }

  if (categoryKey === "graphic-design") {
    return "blue-pill";
  }

  return "";
};
const getOrderConfigKey = (categoryKey = null) => {
  if (categoryKey === "web-development") {
    return "webDevelopment";
  }

  if (categoryKey === "graphic-design") {
    return "graphicDesign";
  }

  return "all";
};
const getOrderedIds = (orderConfig = []) => (
  orderConfig
    .map((entry) => (typeof entry === "string" ? entry : entry?.id))
    .filter((id) => typeof id === "string" && id.trim() !== "")
);
const orderEntries = (entries = [], orderedIds = []) => {
  const rankedIds = new Map(orderedIds.map((id, index) => [String(id), index]));

  return [...entries].sort((leftEntry, rightEntry) => {
    const leftRank = rankedIds.get(String(leftEntry.item.id));
    const rightRank = rankedIds.get(String(rightEntry.item.id));
    const normalizedLeftRank = leftRank ?? Number.MAX_SAFE_INTEGER;
    const normalizedRightRank = rightRank ?? Number.MAX_SAFE_INTEGER;

    if (normalizedLeftRank !== normalizedRightRank) {
      return normalizedLeftRank - normalizedRightRank;
    }

    return leftEntry.workIndex - rightEntry.workIndex;
  });
};

const isLiveUrl = (value = "") => {
  const trimmedValue = getTrimmedValue(value);

  if (!trimmedValue || /\s/.test(trimmedValue)) {
    return false;
  }

  return trimmedValue.startsWith("http://")
    || trimmedValue.startsWith("https://")
    || trimmedValue.includes(".");
};

const getLiveUrl = (value = "") => {
  const trimmedValue = getTrimmedValue(value);

  if (!isLiveUrl(trimmedValue)) {
    return "";
  }

  return trimmedValue.startsWith("http://") || trimmedValue.startsWith("https://")
    ? trimmedValue
    : `https://www.${trimmedValue}`;
};

export default function Portfolio() {
  const workWithIndex = work.map((item, workIndex) => ({ item, workIndex }));
  const tagsByCategory = TAG_FILTER_CATEGORIES.map((category) => ({
    ...category,
    tags: [...new Set(workWithIndex.flatMap(({ item }) => getProjectTagsByFields(item, category.fields)))].sort((a, b) => a.localeCompare(b))
  }));
  const [activeModalIndex, setActiveModalIndex] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeTagCategory, setActiveTagCategory] = useState(null);

  const handleClose = () => setActiveModalIndex(null);
  const handleShow = (index) => setActiveModalIndex(index);

  const toggleTag = (tag) => {
    setSelectedTags((prev) => (
      prev.includes(tag) ? prev.filter((selectedTag) => selectedTag !== tag) : [...prev, tag]
    ));
  };
  const toggleTagCategory = (categoryKey) => {
    setSelectedTags([]);
    setActiveTagCategory((prev) => (prev === categoryKey ? null : categoryKey));
  };
  const clearTags = () => {
    setSelectedTags([]);
    setActiveTagCategory(null);
  };

  const activeCategoryConfig = TAG_FILTER_CATEGORIES.find((category) => category.key === activeTagCategory) || null;
  const categoryFilteredEntries = activeCategoryConfig
    ? workWithIndex.filter(({ item }) => getProjectTagsByFields(item, activeCategoryConfig.fields).length > 0)
    : workWithIndex;
  const matchingEntries = selectedTags.length > 0
    ? categoryFilteredEntries.filter(({ item }) => {
      const projectTags = getProjectTags(item);
      return selectedTags.some((tag) => projectTags.includes(tag));
    })
    : categoryFilteredEntries;
  const activeOrderConfigKey = getOrderConfigKey(activeTagCategory);
  const filteredEntries = orderEntries(matchingEntries, getOrderedIds(workOrder[activeOrderConfigKey] || []));
  const filteredWorkIndexes = filteredEntries.map(({ workIndex }) => workIndex);

  const navigateModal = (direction) => {
    const totalProjects = filteredWorkIndexes.length;

    if (totalProjects < 2 || activeModalIndex === null) {
      return;
    }

    const currentFilteredPosition = filteredWorkIndexes.indexOf(activeModalIndex);
    const currentPosition = currentFilteredPosition >= 0 ? currentFilteredPosition : 0;
    const nextPosition = currentPosition + direction;

    if (nextPosition < 0 || nextPosition >= totalProjects) {
      return;
    }

    setActiveModalIndex(filteredWorkIndexes[nextPosition]);
  };


  const [visible, setVisible] = useState(BASE_MIN_VISIBLE);
  const [minVisible, setMinVisible] = useState(BASE_MIN_VISIBLE);

  const showMoreItems = () => {
    setVisible(preValue => preValue + 3);
  };

  const showLessItems = () => {
    setVisible(preValue => Math.max(preValue - 3, minVisible)); // Ensure minimum visibility based on layout
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateMinVisible = () => {
      const width = window.innerWidth;
      const shouldShowTabletGrid = width >= 992 && width < 1200;
      const nextMin = shouldShowTabletGrid ? TABLET_MIN_VISIBLE : BASE_MIN_VISIBLE;

      setMinVisible(nextMin);
      setVisible(prev => {
        const shouldReduceToBase = prev === TABLET_MIN_VISIBLE && nextMin < TABLET_MIN_VISIBLE;
        if (shouldReduceToBase) {
          return nextMin;
        }

        return Math.max(prev, nextMin); // never drop below required visible count
      });
    };

    updateMinVisible();
    window.addEventListener('resize', updateMinVisible);
    return () => window.removeEventListener('resize', updateMinVisible);
  }, []);

  useEffect(() => {
    if (activeModalIndex === null) return;
    if (filteredWorkIndexes.includes(activeModalIndex)) return;

    setActiveModalIndex(filteredWorkIndexes.length > 0 ? filteredWorkIndexes[0] : null);
  }, [activeModalIndex, filteredWorkIndexes]);

  const displayedEntries = filteredEntries.slice(0, visible);
  const moreItemsAvailable = visible < filteredEntries.length;
  const activeItem = activeModalIndex !== null ? work[activeModalIndex] : null;
  const activeCapabilities = getNonEmptyItems(activeItem?.capabilities);
  const activeScope = getNonEmptyItems(activeItem?.scope);
  const activeFrameworks = getNonEmptyItems(activeItem?.frameworks);
  const activeLiveUrl = getLiveUrl(activeItem?.url);
  const activeBrowserHeading = getTrimmedValue(activeItem?.url || "") || getTrimmedValue(activeItem?.title || "") || "Project";
  const activeImageFit = getImageFit(activeItem?.imageFit);
  const currentFilteredPosition = activeModalIndex !== null ? filteredWorkIndexes.indexOf(activeModalIndex) : -1;
  const hasPreviousProject = currentFilteredPosition > 0;
  const hasNextProject = currentFilteredPosition >= 0 && currentFilteredPosition < filteredWorkIndexes.length - 1;

  return (
    <section className="portfolio-section" id="Portfolio">

      <div className="section-header-wrapper">
        <div className="section-header-container">
          <p className="sub-title">Recent Work</p>
          <h2 className="section-heading">Portfolio</h2>
        </div>
      </div>
      <div className="portfolio-filter-block">
        {!activeTagCategory && (
          <p className="portfolio-filter-prompt">Choose the Red Pill or the Blue Pill</p>
        )}
        <div className="portfolio-tag-filters" aria-label="Portfolio tag filters">
          <div className="portfolio-filter-categories">
            {tagsByCategory.map((category) => (
              <button
                type="button"
                key={category.key}
                className={`portfolio-filter-category ${getCategoryPillClass(category.key)} ${activeTagCategory === category.key ? "is-active" : ""}`.trim()}
                onClick={() => toggleTagCategory(category.key)}
              >
                <span className="pill-shine" aria-hidden="true" />
                <span className="portfolio-filter-category-label">{category.label}</span>
              </button>
            ))}
          </div>

          {activeTagCategory && (
            <div className="portfolio-filter-tags">
              {tagsByCategory.find((category) => category.key === activeTagCategory)?.tags?.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  className={`portfolio-filter-tag ${selectedTags.includes(tag) ? "is-active" : ""}`}
                  onClick={() => toggleTag(tag)}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}

          {(activeTagCategory || selectedTags.length > 0) && (
            <div className="portfolio-filter-actions">
              <button type="button" className="portfolio-filter-clear" onClick={clearTags}>
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="portfolio-section-container">
        {displayedEntries.map(({ item, workIndex }) => {
          const capabilities = getNonEmptyItems(item.capabilities);
          const scope = getNonEmptyItems(item.scope);
          const frameworks = getNonEmptyItems(item.frameworks);
          const cardTags = [...new Set([...capabilities, ...scope, ...frameworks])];
          const browserHeading = getTrimmedValue(item.url) || getTrimmedValue(item.title) || "Project";
          const cardImageFit = getImageFit(item.imageFit);
          const hasVisibleIntro = Boolean(item.intro && item.intro.trim()) && item.showIntro !== false;

          return (
          <div key={workIndex} className="portfolio-card">
            <Button variant="primary" onClick={() => handleShow(workIndex)} className="portfolio-card">
              <div className="portfolio-section-card">
                <div className="portfolio-section-link-wrap">
                  <div className="portfolio-snapshot">
                    <div className="browserTop">
                      <div className="urlWrap">
                        <div className="projectHeadingContainer">
                          <div className="icon-ssl icon-embed"><FontAwesomeIcon icon={faLock} /></div>
                          <div className="projectHeading">
                            {browserHeading}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="portfolio-section-img">
                      <img src={item.src} alt={item.title} style={{ objectFit: cardImageFit }} />
                    </div>
                  </div>

                  <div className="portfolio-section-card-content">
                    <div className="work">
                      {item.title && (
                        <div>
                          <h3 className="portfolio-section-title">
                            {item.title}
                          </h3>
                          {hasVisibleIntro && (
                            <p className="portfolio-intro"><Balancer>{item.intro}</Balancer></p>
                          )}
                        </div>

                      )}

                      {cardTags.length > 0 && (
                        <div className="portfolio-tags" aria-label="Project tags">
                          {cardTags.map((tag, tagIndex) => (
                            <span className="portfolio-tag" key={`${item.id}-tag-${tagIndex}`}>
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className={`portfolio-link ${item.title && hasVisibleIntro ? 'with-margin' : 'without-margin'}`}>
                        <FontAwesomeIcon icon={faLaptopCode} />View Project
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Button>
          </div>
          );
        })}
        {displayedEntries.length === 0 && (
          <div className="portfolio-empty-state">
            No projects match the selected tags.
          </div>
        )}

      </div>
      {activeItem && (
        <>
          <Modal show onHide={handleClose} centered className="portfolioWork">
            <Modal.Header closeButton>
              <Modal.Title>
                <h3 className="portfolio-section-title">
                {activeBrowserHeading}
                </h3>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="portfolio-section-img">
              <img src={activeItem.src} alt="Placeholder" style={{ objectFit: activeImageFit }} />
            </Modal.Body>
            <Modal.Body className="portfolio-section-card-content" >
              <Modal.Body className="work">

                <Container className="modalBody">
                  <Row>
                    <Col xs={12} md={6} lg={3}>
                      <div className="subInfo">
                        {activeItem.position && activeItem.position.trim() !== "" && (
                          <div>
                            <div className="title"><h4>Position:</h4></div>
                            {activeItem.position}
                          </div>
                        )}

                        {activeCapabilities.length > 0 && (
                          <div>
                            <div className="title"><h4>Capabilities:</h4></div>
                            <>
                              {activeCapabilities.map((capability, i) => (
                                <span key={i}>#{capability} </span>
                              ))}
                            </>
                          </div>
                        )}

                        {activeScope.length > 0 && (
                          <div>
                            <div className="title"><h4>Scope:</h4></div>
                            <>
                              {activeScope.map((scopeItem, i) => (
                                <span key={i}>#{scopeItem} </span>
                              ))}
                            </>
                          </div>
                        )}

                        {activeFrameworks.length > 0 && (
                          <div>
                            <div className="title"><h4>Technologies:</h4></div>
                            <>
                              {activeFrameworks.map((framework, i) => (
                                <span key={i}>#{framework} </span>
                              ))}
                            </>
                          </div>
                        )}

                        {activeItem.workWith && activeItem.workWith.some(workWithItem => workWithItem.name || workWithItem.url || workWithItem.title) && (
                          <div>
                            <div className="title"><h4>Worked With:</h4></div>
                            <ul>
                              {activeItem.workWith.map((workWithItem, i) => (
                                (workWithItem.name || workWithItem.url || workWithItem.title) && (
                                  <li key={i}>
                                    {workWithItem.url ? (
                                      <a href={workWithItem.url}>{workWithItem.name}</a>
                                    ) : (
                                      workWithItem.name
                                    )}

                                    {workWithItem.title && (
                                      <span>, {workWithItem.title}</span>
                                    )}
                                  </li>
                                )
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                    </Col>
                    <Col xs={12} md={6} lg={8}>
                      <div className="mainInfo">
                        <h1 className="portfolio-section-title">
                          {activeItem.title}
                        </h1>

                        {activeItem.descriptions && activeItem.descriptions.some(desc => desc.trim() !== "") && (
                          <div className="portfolio-description">
                            <Balancer>
                              <>
                                {activeItem.descriptions.map((description, i) => (
                                  description.trim() !== "" && (
                                    <p key={i}>{description}</p>
                                  )
                                ))}
                              </>
                            </Balancer>
                          </div>
                        )}

                        <div>
                          {activeLiveUrl && (
                            <a href={activeLiveUrl} className="btn btn-primary" target="_blank" rel="noreferrer" ><FontAwesomeIcon icon={faGlobe} /> Visit Live</a>
                          )}
                        </div>
                      </div>

                    </Col>
                  </Row>
                  <Row className="justify-content-center support-row">
                    {activeItem.supportImages && activeItem.supportImages.some(image => image.trim() !== "") && (
                      <Col xs={12} className="d-flex justify-content-center">
                        <div className="support" style={{ width: 'fit-content', maxWidth: '100%', margin: '0 auto' }}>
                          <>
                            {activeItem.supportImages.map((supportImage, i) => (
                              supportImage.trim() !== "" && (
                                <div className="support-img" style={{ display: 'grid', placeItems: 'center', width: '100%' }} key={i}><img src={supportImage} alt={activeItem.title} style={{ display: 'block', float: 'none', height: 'auto', margin: '0 auto', maxWidth: '100%', width: 'auto' }} /></div>
                              )
                            ))}
                          </>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Container>

              </Modal.Body>
            </Modal.Body>
          </Modal>
          <button type="button" className="modalSideNav modalSideNavLeft" onClick={() => navigateModal(-1)} aria-label="Previous project" disabled={!hasPreviousProject}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button type="button" className="modalSideNav modalSideNavRight" onClick={() => navigateModal(1)} aria-label="Next project" disabled={!hasNextProject}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </>
      )}
      <Container>
        <Row>
          <div className="col-12 paginationWorkWrapper">
            <div className="paginationWork">
              <div className="d-flex justify-content-evenly">
                {moreItemsAvailable && (
                  <button className="btn btn-primary viewMorePortfolio" onClick={showMoreItems}> Load More Work</button>
                )}
                {visible > minVisible && (
                  <button className="btn btn-primary viewLessPortfolio" onClick={showLessItems}> Load Less Work</button>
                )}
              </div>

            </div>

          </div>
        </Row>
      </Container>


    </section>
  )
}
